/**
 * The organisation knowledge base: which files may be filed into it, and how
 * ingestion reads each one.
 *
 * A knowledge file is an ordinary `OrganizationPolicy` row carrying the
 * existing `reference` flag (see `Policy.reference`): it rides the same
 * upload, Blob storage, Firecrawl/chunk/embed pipeline and `org-<id>` vector
 * namespace as a policy, so eve's org-scoped `get_information` already finds
 * it. What differs is only what a person may upload, and how its text is
 * read:
 *
 * - `pdf`: the policy path unchanged — pdf-parse for the readable text,
 *   Firecrawl for the searchable markdown.
 * - `document`: an office document Firecrawl parses from its URL.
 * - `text`: plain text read straight from storage, no parser and no credits.
 *
 * Deliberately absent: HTML/SVG and anything else a browser executes. The
 * Blob store is public; a stored page there is a stored script on a domain
 * the workspace links to, and a knowledge base gains nothing from markup the
 * text formats do not already carry.
 *
 * @module shared/knowledgeFiles
 */

export type KnowledgeFileReader = "pdf" | "document" | "text";

export interface KnowledgeFileType {
  /** Lower-case, with the dot. */
  readonly extension: string;
  /** What the file is, for a person: "PDF", "Word", "Markdown". */
  readonly label: string;
  /** Sent when the bytes are stored and served — never inferred from them. */
  readonly contentType: string;
  readonly reader: KnowledgeFileReader;
}

export const KNOWLEDGE_FILE_TYPES: ReadonlyArray<KnowledgeFileType> = [
  { extension: ".pdf", label: "PDF", contentType: "application/pdf", reader: "pdf" },
  {
    extension: ".docx",
    label: "Word",
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    reader: "document",
  },
  { extension: ".doc", label: "Word", contentType: "application/msword", reader: "document" },
  {
    extension: ".odt",
    label: "OpenDocument",
    contentType: "application/vnd.oasis.opendocument.text",
    reader: "document",
  },
  { extension: ".rtf", label: "Rich text", contentType: "application/rtf", reader: "document" },
  { extension: ".txt", label: "Text", contentType: "text/plain; charset=utf-8", reader: "text" },
  { extension: ".md", label: "Markdown", contentType: "text/markdown; charset=utf-8", reader: "text" },
  {
    extension: ".markdown",
    label: "Markdown",
    contentType: "text/markdown; charset=utf-8",
    reader: "text",
  },
  { extension: ".csv", label: "CSV", contentType: "text/csv; charset=utf-8", reader: "text" },
];

/** Same cap as a policy upload: the API's JSON body carries the file as base64. */
export const KNOWLEDGE_MAX_BYTES = 25 * 1024 * 1024;

/** For a file input's `accept`: the extensions, comma-separated. */
export const KNOWLEDGE_ACCEPT = KNOWLEDGE_FILE_TYPES.map((type) => type.extension).join(",");

/** The extensions a person may upload, for copy: "PDF, DOCX, DOC, …". */
export const KNOWLEDGE_EXTENSIONS_LABEL = KNOWLEDGE_FILE_TYPES.map((type) =>
  type.extension.slice(1).toUpperCase(),
)
  .filter((label) => label !== "MARKDOWN")
  .join(", ");

/** The lower-case extension of a file name, with the dot; "" when it has none. */
export const fileExtension = (filename: string): string => {
  const base = filename.slice(filename.lastIndexOf("/") + 1).trim();
  const dot = base.lastIndexOf(".");
  return dot <= 0 ? "" : base.slice(dot).toLowerCase();
};

/** The knowledge type of a file name, or null when the knowledge base does not take it. */
export const knowledgeFileType = (filename: string): KnowledgeFileType | null =>
  KNOWLEDGE_FILE_TYPES.find((type) => type.extension === fileExtension(filename)) ?? null;

/**
 * Why a file cannot be filed, or null when it can. One wording for the
 * browser's pre-check and the API's guard, so a person sees the same reason
 * whichever refuses first.
 */
export const knowledgeFileProblem = (file: { readonly name: string; readonly size: number }): string | null =>
  knowledgeFileType(file.name) === null
    ? `Unsupported type — use ${KNOWLEDGE_EXTENSIONS_LABEL}`
    : file.size === 0
      ? "Empty file"
      : file.size > KNOWLEDGE_MAX_BYTES
        ? `Over ${KNOWLEDGE_MAX_BYTES / (1024 * 1024)} MB`
        : null;

/**
 * The content type a stored document is served with. Anything not in the
 * table — every policy is a PDF — keeps the policy default, so this widens
 * downloads for knowledge files without changing a single existing one.
 */
export const storedDocumentContentType = (filename: string): string =>
  knowledgeFileType(filename)?.contentType ?? "application/pdf";

/**
 * Whether a stored document may be shown inline in the browser. Only a PDF:
 * the workspace embeds those in its viewer, and every other type downloads.
 */
export const storedDocumentInline = (filename: string): boolean =>
  (knowledgeFileType(filename)?.reader ?? "pdf") === "pdf";

/**
 * The domain API's type rule for a library upload: a policy or procedure is a
 * PDF; a knowledge-base file (`reference`) may be any type in the table.
 * Yields the content type to store it with, or the refusal to answer.
 */
export const libraryUploadType = (
  filename: string,
  reference: boolean,
): { readonly contentType: string } | { readonly error: string } => {
  if (!reference) {
    return fileExtension(filename) === ".pdf"
      ? { contentType: "application/pdf" }
      : { error: "Only PDF policies are supported." };
  }
  const type = knowledgeFileType(filename);
  return type === null
    ? { error: `Knowledge files must be ${KNOWLEDGE_EXTENSIONS_LABEL}.` }
    : { contentType: type.contentType };
};
